import { useEffect, useState } from 'react';
import { InAppContentDefinition, MESSAGE_FORMAT, Position } from 'modules/graphql/types';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { AnimatedRefreshIcon, Play } from 'ui/icons';
import { SegmentedControl } from 'ui/segmentedControl';
import { spacing, Subtitle1, colors } from 'ui/theme';

import EmptyStateWaves from './EmptyStateWaves';
import { Preview } from './Preview';
import { useGetViewOptions } from './hooks/useGetViewOptions';
import {
  PreviewContainer,
  PreviewControls,
  PreviewFooter,
  PreviewPaneContainer,
  PreviewRefreshButton,
  RawDataContainer,
  RawDataHeader,
} from '../../common';
import { TemplateEditViews } from '../../common/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useInAppPreviewContext } from '../context/InAppPreviewContext';
import { PreviewHeader } from '../../common/PreviewHeader';
import { RawPreviewField } from '../../common/RawPreviewField';
import {
  ANIMATION,
  ANIMATION_DELAY,
  ANIMATION_DURATION,
  IN_APP_VIEW_OPTIONS,
  SETTINGS_DISPLAY_CONTENT,
} from '../constants';
import { SETTINGS_DISPLAY_CONTENT as CONTENT } from '../../common/constants';
import { InAppPreviewContentContainer } from './styles';
import { getPositionFromInAppDisplaySetting } from '../utils';

const EmptyState = styled.span`
  inset: 0;
  position: absolute;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const RawDataState = styled(EmptyState)`
  background-color: ${colors.backgroundPrimary};
`;

const EmptyStateText = styled(Subtitle1)`
  color: ${colors.grey35};
  text-align: center;
  margin-top: ${spacing.double};
`;

const StyledPlay = styled(Play)`
  margin-left: ${spacing.eighth};
`;

const StyledIconButton = styled(IconButton)`
  padding: ${spacing.eighth};
`;

export const TEMPLATE_EDITOR_IN_APP_PREVIEW_PANE_ID = 'template-editor-in-app-preview-pane';

export const PreviewPane = () => {
  const { view } = useQueryParamContext();
  const {
    isLoading: isRefreshing,
    messageRawDataPayload,
    onRefreshPreview,
    previewViewOption,
    setPreviewViewOption,
  } = useInAppPreviewContext();
  const { liveTemplate } = useEditableFieldsContext();
  const viewOptions = useGetViewOptions();
  const inAppMediumData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { inAppDisplaySettings, messageFormat } = inAppMediumData;
  const position = getPositionFromInAppDisplaySetting(inAppDisplaySettings) || Position.Full;
  const { shouldAnimate } = inAppDisplaySettings || {};
  const isPreviewWithData = view === TemplateEditViews.preview;
  const [canTriggerAnimate, setCanTriggerAnimate] = useState<boolean>(true);
  const [animation, setAnimation] = useState<ANIMATION | null>(null);

  // /** Copied over from WebInApp */
  let timeout: null | NodeJS.Timeout = null;

  useEffect(() => {
    return () => {
      /* clear timeout when component unmounts */
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  const _setAnimation = (newAnimation: ANIMATION) => {
    if (!shouldAnimate || !canTriggerAnimate) {
      /* user didn't toggle animations on. just abort early in this case. */
      return;
    }
    setCanTriggerAnimate(false);
    setAnimation(newAnimation);

    timeout = global.setTimeout(() => {
      setAnimation(null);
      setCanTriggerAnimate(true);
      clearTimeout(timeout as NodeJS.Timeout);
    }, ANIMATION_DURATION + ANIMATION_DELAY);
  };

  return (
    <PreviewPaneContainer>
      <PreviewContainer
        data-test={TEMPLATE_EDITOR_IN_APP_PREVIEW_PANE_ID}
        isPreviewWithData={isPreviewWithData}
      >
        <PreviewHeader />
        <InAppPreviewContentContainer
          data-test="template-editor-in-app-preview-content"
          $isWebPreview={previewViewOption === IN_APP_VIEW_OPTIONS.web}
        >
          {messageFormat !== MESSAGE_FORMAT.Json ? (
            <Preview animation={animation} selectedViewOption={previewViewOption} />
          ) : (
            <RawDataState data-test="template-editor-in-app-empty-state">
              <EmptyStateWaves />
              <EmptyStateText>{SETTINGS_DISPLAY_CONTENT.rawDataMessage}</EmptyStateText>
            </RawDataState>
          )}
        </InAppPreviewContentContainer>
        {isPreviewWithData && (
          <PreviewRefreshButton
            data-test="template-editor-in-app-preview-refresh-button"
            onClick={() => onRefreshPreview()}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            <AnimatedRefreshIcon $isAnimating={isRefreshing} />
          </PreviewRefreshButton>
        )}
        <PreviewFooter data-test="template-editor-in-app-preview-footer">
          <PreviewControls>
            <SegmentedControl
              data-test="template-editor-in-app-segmented-control"
              onSelect={setPreviewViewOption}
              options={viewOptions}
              value={previewViewOption}
            />
            {shouldAnimate && (
              <StyledIconButton
                onClick={() =>
                  _setAnimation(
                    position === Position.Bottom || position === Position.Top
                      ? ANIMATION.slideIn
                      : ANIMATION.fadeIn,
                  )
                }
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                <StyledPlay />
              </StyledIconButton>
            )}
          </PreviewControls>
        </PreviewFooter>
      </PreviewContainer>
      {isPreviewWithData && (
        <RawDataContainer>
          <RawDataHeader>{CONTENT.rawData.labelText}</RawDataHeader>
          <RawPreviewField populatedPayload={messageRawDataPayload} />
        </RawDataContainer>
      )}
    </PreviewPaneContainer>
  );
};

export default PreviewPane;
